import { useSelect } from '@refinedev/antd';
import { useList } from '@refinedev/core';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import type { ValueEditorProps } from 'react-querybuilder';
import { AntDValueEditor } from '@react-querybuilder/antd';
import {
  ASYNC_FIELD_RESOURCE_MAP,
  DATE_FIELDS,
  MULTI_VALUE_OPERATORS,
  NO_VALUE_OPERATORS,
  RANGE_OPERATORS,
} from './fieldMappings';

const toDefaultValue = (value: unknown, multiple: boolean): unknown => {
  if (multiple) {
    if (Array.isArray(value)) return value;
    return value === '' || value == null ? [] : [value];
  }
  return value === '' || value == null ? undefined : value;
};

/** Selected value(s) as a flat array, regardless of single/multi operator. */
const toArray = (value: unknown): (string | number)[] => {
  if (Array.isArray(value)) return value as (string | number)[];
  return value === '' || value == null ? [] : [value as string | number];
};

type Option = { label: string; value: string | number };

const mergeOptions = (preload: Option[], search: Option[]): Option[] => {
  const seen = new Set<string | number>();
  const out: Option[] = [];
  for (const opt of [...preload, ...search]) {
    if (opt && !seen.has(opt.value)) {
      seen.add(opt.value);
      out.push(opt);
    }
  }
  return out;
};

/** Resolve the authoritative field name (props.field may be normalized by RQB). */
const fieldNameOf = (props: ValueEditorProps): string =>
  props.fieldData?.name ?? props.field;

/** Async autocomplete editor backed by a Refine resource. */
const AsyncResourceValueEditor = (props: ValueEditorProps) => {
  const { operator, value, handleOnChange, disabled } = props;
  const cfg = ASYNC_FIELD_RESOURCE_MAP[fieldNameOf(props)]!;
  const multiple = MULTI_VALUE_OPERATORS.has(operator);
  const selected = toArray(value);

  // Searchable options.
  const { selectProps } = useSelect({
    resource: cfg.resource,
    optionLabel: cfg.optionLabel as never,
    optionValue: cfg.optionValue as never,
    onSearch: (search) => [{ field: 'q', operator: 'contains', value: search }],
  });

  // Preload labels for already-selected values. Matches on `optionValue`
  // (e.g. `code`), not the resource `id`, so non-id fields resolve correctly.
  const { result: preload } = useList<Record<string, unknown>>({
    resource: cfg.resource,
    filters: [{ field: cfg.optionValue, operator: 'in', value: selected }],
    pagination: { pageSize: Math.max(selected.length, 1), mode: 'server' },
    queryOptions: { enabled: selected.length > 0 },
  });

  const preloadOptions: Option[] = (preload?.data ?? []).map((record) => ({
    label: String(record[cfg.optionLabel] ?? record[cfg.optionValue]),
    value: record[cfg.optionValue] as string | number,
  }));

  const options = mergeOptions(
    preloadOptions,
    (selectProps.options as Option[]) ?? []
  );

  return (
    <Select
      style={{ minWidth: 260 }}
      mode={multiple ? 'multiple' : undefined}
      value={toDefaultValue(value, multiple)}
      options={options}
      loading={selectProps.loading}
      onSearch={selectProps.onSearch}
      onChange={(val) => handleOnChange(val)}
      showSearch
      allowClear
      filterOption={false}
      disabled={disabled}
      getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
    />
  );
};

/** Date / date-range editor for `created_at` and `updated_at`. */
const DateValueEditor = (props: ValueEditorProps) => {
  const { operator, value, handleOnChange, disabled } = props;

  if (RANGE_OPERATORS.has(operator)) {
    const parts = Array.isArray(value)
      ? value
      : typeof value === 'string' && value
        ? value.split(',')
        : [];
    const range: [dayjs.Dayjs | null, dayjs.Dayjs | null] = [
      parts[0] ? dayjs(parts[0]) : null,
      parts[1] ? dayjs(parts[1]) : null,
    ];
    return (
      <DatePicker.RangePicker
        showTime
        disabled={disabled}
        value={range}
        onChange={(dates) =>
          handleOnChange(
            dates && dates[0] && dates[1]
              ? [dates[0].toISOString(), dates[1].toISOString()]
              : ''
          )
        }
      />
    );
  }

  return (
    <DatePicker
      showTime
      disabled={disabled}
      value={value ? dayjs(value as string) : null}
      onChange={(date) => handleOnChange(date ? date.toISOString() : '')}
    />
  );
};

/**
 * Routes each rule's value editor: async resource → autocomplete,
 * date field → date picker, everything else → Ant Design default editor.
 */
export const DispatchValueEditor = (props: ValueEditorProps) => {
  const fieldName = fieldNameOf(props);
  if (NO_VALUE_OPERATORS.has(props.operator)) {
    return null;
  }
  if (DATE_FIELDS.has(fieldName)) {
    return <DateValueEditor {...props} />;
  }
  if (ASYNC_FIELD_RESOURCE_MAP[fieldName]) {
    return <AsyncResourceValueEditor {...props} />;
  }
  return <AntDValueEditor {...props} />;
};
